/**
 * Страница тематического узла — /blog/<тема>[-<район>]/ (до 03.09.2026 /articles/).
 *
 * Узел существует ради связности: до него ведёт указатель, от него — ссылки на
 * статьи темы. Прежний единственный указатель перечислял 5 242 ссылки на одной
 * странице; сторож связности его принимал, но вес по каждой ссылке был
 * ничтожен, и ни одна не говорила, о чём страница.
 *
 * Список приходит из /_articles/_hub-<id>.json. Готовую страницу порождает
 * scripts/generate-legacy-pages.mjs, он же кладёт данные в window.__HUB__ —
 * поэтому первая отрисовка совпадает с уже отданной разметкой.
 */
import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';

interface HubPage {
  url: string;
  title: string;
}

interface Hub {
  id: string;
  url: string;
  title: string;
  pages: HubPage[];
}

declare global {
  interface Window {
    __HUB__?: Hub;
  }
}

function useHub(id: string): Hub | null | undefined {
  const inline =
    typeof window !== 'undefined' && window.__HUB__?.id === id ? window.__HUB__ : null;
  const [hub, setHub] = useState<Hub | null | undefined>(inline ?? undefined);

  useEffect(() => {
    if (inline) { setHub(inline); return; }
    let alive = true;
    setHub(undefined);
    fetch(`/_articles/_hub-${id}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: Hub) => { if (alive) setHub(d); })
      .catch(() => { if (alive) setHub(null); });
    return () => { alive = false; };
  }, [id, inline]);

  return hub;
}

export default function ArticleHubPage({ id }: { id: string }) {
  const hub = useHub(id);

  useEffect(() => {
    if (typeof document !== 'undefined' && hub) {
      document.title = `${hub.title} | Morgan Legal Group New York`;
    }
  }, [hub]);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main id="article-hub">
        <section className="relative bg-navy pt-28 lg:pt-32 pb-14 lg:pb-16">
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <span className="mx-2 text-gold/60">/</span>
              <a href="/blog/" className="hover:text-gold transition-colors">Article Library</a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">{hub?.title ?? ''}</span>
            </nav>
            <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1]">
              {hub?.title ?? ''}
            </h1>
            <p className="font-body text-white/80 mt-5">{hub?.pages.length ?? 0} articles.</p>
          </div>
        </section>
        <section className="py-14 lg:py-20 bg-cream">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 font-body text-navy/85">
            <ul className="space-y-2">
              {(hub?.pages ?? []).map((p) => (
                <li key={p.url}>
                  <a
                    className="text-navy underline decoration-gold/60 underline-offset-2 hover:text-gold"
                    href={p.url}
                  >
                    {p.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
